import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Pagination, PaginationEventType } from '@ui/v2/Pagination/Pagination'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { getDomainName } from '@utils/cookie.utils'
import { useNotificationUnread } from '@utils/notifications'

import { Notification, getNotificationSearchQueryById } from '@model/Notification'
import { NotificationPlatformEnum } from '@model/PlatformTypeEnum'

import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
} from '@hooks/useScrollToElementIds'
import { safeLocalStorage } from '@hooks/useStorage'

import { Loading } from '@shared/components/Loading/Loading'

import { NotificationPreview } from './NotificationPreview'
import { NotificationsNotFound } from './NotificationsNotFound'

import styles from './NotificationCenter.module.scss'

type PaginationType = { pageIndex: number; pageSize: number; cardId: string }

export enum NotificationType {
  ALL = '',
  GENERAL = '1',
  PLATFORM = '2',
  PROMOTIONAL = '3',
}

interface Props {
  type?: NotificationType
}

export const NotificationCenter: React.FC<Props> = ({ type = NotificationType.ALL }) => {
  const locale = useSessionLanguage()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const [removingNotification, setRemovingNotification] = useState<Notification>()
  const { refreshAllNotifications } = useNotificationUnread()
  const { product: tickmillProduct } = useProductReadContext()

  const { setHeaderActions } = useOutletContext<{
    setHeaderActions: (v: { allowMarkAllRead: boolean; onMarkAllRead: () => void }) => void
  }>()

  const PAGINATION_KEY = 'pagination'
  const TIMEOUT_DURATION = 2000

  useEffect(() => {
    const paginationString = safeLocalStorage.getItem(PAGINATION_KEY)
    if (paginationString) {
      const pagination = JSON.parse(paginationString) as PaginationType
      if (pagination) {
        setPageQuery!((prev: any) => ({
          ...prev,
          pageIndex: pagination?.pageIndex,
          pageSize: pagination.pageSize,
        }))
        setTimeout(() => safeLocalStorage.removeItem(PAGINATION_KEY), TIMEOUT_DURATION)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchKey = { locale, type, tickmillProduct }

  const { data, meta, setPageQuery, isLoading, pageQuery, forceRefresh } = usePaginatedQuery({
    queryKey: queryKeys.notification.list(searchKey),
    queryFn: (pageQuery) =>
      apiClient.getNotifications({
        search: getNotificationSearchQueryById(type, {
          TickmillProduct: tickmillProduct,
          Platform: NotificationPlatformEnum.WEB,
          Language: locale,
        }),
        ...pageQuery,
        languageId: locale,
        operator: 'AND',
        domain: getDomainName(true),
      }),
  })

  const [notifications, setNotifications] = useState<Notification[]>(data)

  useEffect(() => {
    setNotifications(data)
  }, [data])

  useEffect(() => {
    if (meta && !notifications.length && meta?.pageIndex !== 1) {
      setPageQuery!({ ...pageQuery, pageIndex: meta.pageIndex - 1 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, notifications])

  const openNotification = (id: string, clientNotificationId?: string) => {
    safeLocalStorage.setItem(
      PAGINATION_KEY,
      JSON.stringify({ pageIndex: meta?.pageIndex, pageSize: meta?.pageSize, cardId: id })
    )
    navigate(
      `/profile/notifications/${id}${clientNotificationId ? `/${clientNotificationId}` : ''}`
    )
  }

  const removeNotification = async (notification: Notification) => {
    setRemovingNotification(notification)
    setTimeout(async () => {
      setNotifications((notifications) => notifications.filter((n) => n !== notification))
      await apiClient.deleteNotification(notification.id, notification.clientNotificationId)
      await queryClient.invalidateQueries({ queryKey: ['notification'] })
      await forceRefresh()
      await forceRefresh()
      await refreshAllNotifications()
    }, 550)
  }

  const readAll = useCallback(async () => {
    setNotifications((notifications) =>
      notifications.map((notification) => ({
        ...notification,
        isRead: true,
      }))
    )
    await apiClient.readAllNotifications(
      notifications.map((notification) => ({
        notificationId: notification.id,
        clientNotificationId: notification.clientNotificationId || null,
      }))
    )
    await queryClient.invalidateQueries({ queryKey: ['notification'] })
    await refreshAllNotifications()
    await forceRefresh()
  }, [apiClient, forceRefresh, notifications, queryClient, refreshAllNotifications])

  const readNotification = async (notification: Notification) => {
    if (!notification.isRead) {
      setNotifications((notifications) =>
        notifications.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
      )
      await apiClient.readNotification(notification.id, notification.clientNotificationId)
      await queryClient.invalidateQueries({ queryKey: ['notification'] })
    }
  }

  const allowReadAll = useMemo(() => notifications?.some((n) => !n.isRead), [notifications])

  useEffect(() => {
    setHeaderActions({
      allowMarkAllRead: allowReadAll,
      onMarkAllRead: readAll,
    })
  }, [allowReadAll, readAll, setHeaderActions])

  useEffect(() => {
    return () => {
      setHeaderActions({
        allowMarkAllRead: false,
        onMarkAllRead: () => {},
      })
    }
  }, [setHeaderActions])

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)
  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.NotificationCenterHeader,
    isPaginationEntrySelected,
    isLoading,
    setIsPaginationEntrySelected
  )

  const getCardId = () => {
    const s = safeLocalStorage.getItem(PAGINATION_KEY)
    if (s) {
      const { cardId } = JSON.parse(s) as PaginationType
      return cardId
    }
    return ScrollToIds.NotificationCenterItemMiddle
  }

  useScrollAfterLoad(
    getCardId(),
    isLoading,
    meta?.pageSize,
    !!safeLocalStorage.getItem(PAGINATION_KEY)
  )

  const handlePageChange = (
    pageIndex: number,
    pageSize?: number,
    eventType?: PaginationEventType
  ) => {
    if (eventType === PaginationEventType.ENTRIES_CHANGED) {
      setIsPaginationEntrySelected(true)
    }
    setPageQuery!({
      ...pageQuery,
      pageIndex,
      pageSize: pageSize ?? pageQuery?.pageSize,
    })
  }

  return (
    <div className={styles.notifications}>
      <Loading showLoadingIcon isLoading={isLoading}>
        {notifications.length === 0 ? (
          <NotificationsNotFound />
        ) : (
          notifications.map((notification) => (
            <NotificationPreview
              className={classNames({
                [styles.removing]: removingNotification === notification,
              })}
              cardId={notification.id}
              key={`${notification.id}-${notification.clientNotificationId}`}
              read={() => readNotification(notification)}
              open={() => openNotification(notification.id, notification?.clientNotificationId)}
              remove={() => removeNotification(notification)}
              notification={notification}
              showCloseModal={true}
            />
          ))
        )}
      </Loading>

      {meta && (
        <Pagination
          scrollToHeaderId={ScrollToIds.NotificationCenterHeader}
          pageData={meta}
          isLoading={isLoading}
          onPageChanged={handlePageChange}
        />
      )}
    </div>
  )
}
